import React from 'react';
import { format, startOfMonth, endOfMonth, subMonths, addMonths, isBefore, isAfter } from 'date-fns';
import { PieChart, BarChart, DollarSign, TrendingUp, Calendar as CalendarIcon, ChevronLeft, ChevronRight, Scale } from 'lucide-react';
import useEvents from '../hooks/useEvents';
import { CategoryTotal, TransactionCategory } from '../types';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';

// Category Colors
const CATEGORY_COLORS: Record<TransactionCategory, string> = {
  food: 'bg-orange-500',
  groceries: 'bg-green-500',
  dining: 'bg-yellow-500',
  entertainment: 'bg-purple-500',
  shopping: 'bg-pink-500',
  transportation: 'bg-blue-500',
  utilities: 'bg-gray-500',
  housing: 'bg-indigo-500',
  healthcare: 'bg-red-500',
  medical: 'bg-red-600',
  insurance: 'bg-blue-600',
  education: 'bg-teal-500',
  travel: 'bg-cyan-500',
  personal: 'bg-violet-500',
  gifts: 'bg-rose-500',
  savings: 'bg-emerald-500',
  debt: 'bg-red-700',
  subscription: 'bg-blue-400',
  pet: 'bg-amber-500',
  other: 'bg-gray-400'
};

// Category Labels
const CATEGORY_LABELS: Record<TransactionCategory, string> = {
  food: 'Food & Dining',
  groceries: 'Groceries',
  dining: 'Restaurants',
  entertainment: 'Entertainment',
  shopping: 'Shopping',
  transportation: 'Transportation',
  utilities: 'Utilities',
  housing: 'Housing',
  healthcare: 'Healthcare',
  medical: 'Medical Expenses',
  insurance: 'Insurance',
  education: 'Education',
  travel: 'Travel',
  personal: 'Personal',
  gifts: 'Gifts',
  savings: 'Savings',
  debt: 'Debt',
  subscription: 'Subscriptions',
  pet: 'Pet & Vet Expenses',
  other: 'Other'
};

// Recommended spending percentages based on the 50/30/20 rule and common financial advice
const RECOMMENDED_PERCENTAGES: Partial<Record<TransactionCategory, { min: number; max: number }>> = {
  housing: { min: 25, max: 35 },
  utilities: { min: 5, max: 10 },
  groceries: { min: 10, max: 15 },
  transportation: { min: 10, max: 15 },
  healthcare: { min: 5, max: 10 },
  insurance: { min: 10, max: 15 },
  savings: { min: 20, max: 30 },
  debt: { min: 0, max: 15 },
  entertainment: { min: 5, max: 10 },
  personal: { min: 5, max: 10 },
  shopping: { min: 5, max: 10 },
  subscription: { min: 0, max: 5 },
  pet: { min: 3, max: 8 }
};

export default function SpendingAnalytics() {
  const { events } = useEvents();
  const { user } = useAuth();
  const [selectedMonth, setSelectedMonth] = React.useState(new Date());
  const [bucketTransactions, setBucketTransactions] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);

  // Fetch bucket transactions
  React.useEffect(() => {
    const fetchBucketTransactions = async () => {
      if (!user) return;

      try {
        const bucketsRef = collection(db, 'budgetBuckets');
        const q = query(bucketsRef, where('userId', '==', user.uid));
        const snapshot = await getDocs(q);
        
        const transactions: any[] = [];
        snapshot.docs.forEach(doc => {
          const bucket = doc.data();
          if (bucket.transactions) {
            bucket.transactions.forEach((transaction: any) => {
              if (transaction.amount < 0) { // Only include spending (negative amounts)
                transactions.push({
                  ...transaction,
                  category: bucket.category,
                  date: transaction.date.toDate(),
                  amount: Math.abs(transaction.amount) // Convert to positive for analytics
                });
              }
            });
          }
        });

        setBucketTransactions(transactions);
      } catch (error) {
        console.error('Error fetching bucket transactions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBucketTransactions();
  }, [user]);

  const calculateMonthlyIncome = (startDate: Date, endDate: Date): number => {
    if (!events) return 0;
    
    return events
      .filter(event => 
        event.type === 'payday' &&
        event.date >= startDate &&
        event.date <= endDate
      )
      .reduce((sum, event) => sum + event.amount, 0);
  };

  const calculateAdjustments = (startDate: Date, endDate: Date): { positive: number; negative: number } => {
    if (!events) return { positive: 0, negative: 0 };

    const adjustments = events
      .filter(event => 
        event.type === 'adjustment' &&
        event.date >= startDate &&
        event.date <= endDate
      )
      .map(event => {
        const adjustmentAmount = event.adjustmentAmount || 0;
        return adjustmentAmount;
      });

    return adjustments.reduce((acc, amount) => {
      if (amount > 0) {
        acc.positive += amount;
      } else {
        acc.negative += Math.abs(amount);
      }
      return acc;
    }, { positive: 0, negative: 0 });
  };

  const calculateCategoryTotals = (startDate: Date, endDate: Date): CategoryTotal[] => {
    if (!events) return [];

    const categoryTotals: Record<TransactionCategory, CategoryTotal> = {} as Record<TransactionCategory, CategoryTotal>;

    // Add regular bill transactions
    events
      .filter(event => 
        event.type === 'bill' &&
        event.category &&
        event.date >= startDate &&
        event.date <= endDate
      )
      .forEach(event => {
        const category = event.category as TransactionCategory;
        if (!categoryTotals[category]) {
          categoryTotals[category] = {
            category,
            total: 0,
            count: 0,
            transactions: []
          };
        }
        categoryTotals[category].total += event.amount;
        categoryTotals[category].count += 1;
        categoryTotals[category].transactions.push(event);
      });

    // Add bucket transactions
    bucketTransactions
      .filter(transaction => 
        transaction.date >= startDate &&
        transaction.date <= endDate
      )
      .forEach(transaction => {
        const category = transaction.category as TransactionCategory;
        if (!categoryTotals[category]) {
          categoryTotals[category] = {
            category,
            total: 0,
            count: 0,
            transactions: []
          };
        }
        categoryTotals[category].total += transaction.amount;
        categoryTotals[category].count += 1;
        categoryTotals[category].transactions.push(transaction);
      });

    return Object.values(categoryTotals).sort((a, b) => b.total - a.total);
  };

  const getPercentageStatus = (category: TransactionCategory, percentage: number): string => {
    const recommended = RECOMMENDED_PERCENTAGES[category];
    if (!recommended) return '';

    if (percentage < recommended.min) return 'text-yellow-600';
    if (percentage > recommended.max) return 'text-red-600';
    return 'text-green-600';
  };

  const handlePrevMonth = () => {
    setSelectedMonth(subMonths(selectedMonth, 1));
  };

  const handleNextMonth = () => {
    const nextMonth = addMonths(selectedMonth, 1);
    if (isBefore(nextMonth, addMonths(new Date(), 1))) {
      setSelectedMonth(nextMonth);
    }
  };

  const monthStart = startOfMonth(selectedMonth);
  const monthEnd = endOfMonth(selectedMonth);
  const categoryTotals = calculateCategoryTotals(monthStart, monthEnd);
  const totalSpending = categoryTotals.reduce((sum, cat) => sum + cat.total, 0);
  const monthlyIncome = calculateMonthlyIncome(monthStart, monthEnd);
  const adjustments = calculateAdjustments(monthStart, monthEnd);
  const spendingPercentage = monthlyIncome > 0 ? (totalSpending / monthlyIncome) * 100 : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent flex items-center">
            <BarChart className="w-6 h-6 mr-2" />
            Spending Analytics
          </h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrevMonth}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-lg font-medium min-w-[150px] text-center">
              {format(selectedMonth, 'MMMM yyyy')}
            </span>
            <button
              onClick={handleNextMonth}
              className="p-2 hover:bg-gray-100 rounded-full"
              disabled={isAfter(selectedMonth, new Date())}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="glass-effect rounded-xl hover:scale-105 transition-all duration-300 animate-fadeIn p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Monthly Income</p>
                <p className="text-3xl font-bold text-green-600">
                  ${monthlyIncome.toFixed(2)}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <DollarSign className="w-8 h-8 text-green-600" />
              </div>
            </div>
          </div>

          <div className="glass-effect rounded-xl hover:scale-105 transition-all duration-300 animate-fadeIn p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Spending</p>
                <p className="text-3xl font-bold text-indigo-600">
                  ${totalSpending.toFixed(2)}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {spendingPercentage.toFixed(1)}% of Income
                </p>
              </div>
              <div className="p-3 bg-indigo-100 rounded-full">
                <DollarSign className="w-8 h-8 text-indigo-600" />
              </div>
            </div>
          </div>

          <div className="glass-effect rounded-xl hover:scale-105 transition-all duration-300 animate-fadeIn p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Balance Adjustments</p>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-green-600">+${adjustments.positive.toFixed(2)}</span>
                  <span className="text-sm text-gray-400">/</span>
                  <span className="text-sm text-red-600">-${adjustments.negative.toFixed(2)}</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Net: ${(adjustments.positive - adjustments.negative).toFixed(2)}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <Scale className="w-8 h-8 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="glass-effect rounded-xl hover:scale-105 transition-all duration-300 animate-fadeIn p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Categories Used</p>
                <p className="text-3xl font-bold text-purple-600">
                  {categoryTotals.length}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <PieChart className="w-8 h-8 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="glass-effect rounded-xl hover:scale-105 transition-all duration-300 animate-fadeIn overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Spending by Category</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {categoryTotals.map((category) => {
                const percentage = monthlyIncome > 0 ? (category.total / monthlyIncome) * 100 : 0;
                const percentageStatus = getPercentageStatus(category.category, percentage);
                const recommended = RECOMMENDED_PERCENTAGES[category.category];

                return (
                  <div key={category.category}>
                    <div className="flex justify-between items-center mb-1">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full ${CATEGORY_COLORS[category.category]} mr-2`} />
                        <span className="text-sm font-medium text-gray-900">
                          {CATEGORY_LABELS[category.category]}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-medium text-gray-900">
                          ${category.total.toFixed(2)}
                        </span>
                        <span className={`text-sm ml-2 ${percentageStatus}`}>
                          ({percentage.toFixed(1)}% of income)
                          {recommended && (
                            <span className="text-xs text-gray-500 ml-1">
                              (Recommended: {recommended.min}-{recommended.max}%)
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`${CATEGORY_COLORS[category.category]} h-2 rounded-full transition-all`}
                        style={{ width: `${(category.total / totalSpending) * 100}%` }}
                      />
                    </div>
                  </div>
                );
              })}

              {categoryTotals.length === 0 && (
                <div className="text-center py-12">
                  <BarChart className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No spending data</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Start categorizing your transactions to see spending analytics.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}